import { useEffect, useMemo, useState } from 'react';
import { FiFileText, FiFilter, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import Pagination from '../components/Pagination';
import { CRUD_UI } from '../components/crudUi';
import { formatDateTime } from '../utils/dateFormat';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    action: '',
    start_date: '',
    end_date: '',
  });

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      ...(filters.action ? { action: filters.action } : {}),
      ...(filters.start_date ? { start_date: filters.start_date } : {}),
      ...(filters.end_date ? { end_date: filters.end_date } : {}),
    }),
    [currentPage, itemsPerPage, filters]
  );

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/audit-logs', { params: queryParams });
      const data = response.data?.data || [];
      const pagination = response.data?.pagination || {};
      setLogs(data);
      setTotalItems(Number(pagination.total || 0));
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || 'Không thể tải nhật ký audit_logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [queryParams]);

  const renderDetails = (details) => {
    if (!details) return '-';
    if (typeof details === 'string') return details;
    try {
      return JSON.stringify(details);
    } catch {
      return '-';
    }
  };

  return (
    <div className={CRUD_UI.page}>
      <div className={CRUD_UI.filterCard}>
        <div className={CRUD_UI.filterHeader}>
          <FiFilter className="text-slate-500 text-xl" />
          <span className="text-sm font-semibold text-slate-900">Bộ lọc nhật ký audit_logs</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Hành động</label>
            <div className={CRUD_UI.searchWrap}>
              <FiSearch className={CRUD_UI.searchIcon} />
              <input
                type="text"
                className={CRUD_UI.searchInput}
                placeholder="Ví dụ: CREATE_USER, HIS_ORDER..."
                value={filters.action}
                onChange={(e) => {
                  setCurrentPage(1);
                  setFilters((prev) => ({ ...prev, action: e.target.value }));
                }}
              />
            </div>
          </div>
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Từ ngày</label>
            <input
              type="date"
              className={CRUD_UI.filterInput}
              value={filters.start_date}
              onChange={(e) => {
                setCurrentPage(1);
                setFilters((prev) => ({ ...prev, start_date: e.target.value }));
              }}
            />
          </div>
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Đến ngày</label>
            <input
              type="date"
              className={CRUD_UI.filterInput}
              value={filters.end_date}
              onChange={(e) => {
                setCurrentPage(1);
                setFilters((prev) => ({ ...prev, end_date: e.target.value }));
              }}
            />
          </div>
        </div>
      </div>

      <div className={CRUD_UI.tableViewportShell}>
        <div className={CRUD_UI.tableViewportScroll}>
          <table className={CRUD_UI.table}>
            <thead className={CRUD_UI.tableHead}>
              <tr>
                <th className={CRUD_UI.tableHeaderCell}>Thời gian</th>
                <th className={CRUD_UI.tableHeaderCell}>Người dùng</th>
                <th className={CRUD_UI.tableHeaderCell}>Site</th>
                <th className={CRUD_UI.tableHeaderCell}>Hành động</th>
                <th className={CRUD_UI.tableHeaderCell}>Đối tượng</th>
                <th className={CRUD_UI.tableHeaderCell}>Chi tiết</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className={CRUD_UI.emptyCell}>
                    <p className="text-slate-500 text-sm">Đang tải nhật ký audit_logs...</p>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className={CRUD_UI.emptyCell}>
                    <div className="flex flex-col items-center gap-2">
                      <FiFileText className="text-5xl text-slate-300" />
                      <p className="text-slate-500 text-sm">Không có dữ liệu nhật ký audit_logs</p>
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className={CRUD_UI.tableRow}>
                    <td className="px-3 py-2 text-sm text-slate-700">{formatDateTime(log.timestamp)}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{log.username || '-'}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{log.site_id || '-'}</td>
                    <td className="px-3 py-2 text-sm">
                      <code className="px-1.5 py-0.5 rounded bg-slate-100 text-xs text-slate-700">{log.action || '-'}</code>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">
                      {log.object_type || '-'}
                      {log.object_id ? ` / ${log.object_id}` : ''}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[460px] break-words">
                      {renderDetails(log.details)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalItems > 0 && (
        <div className={CRUD_UI.paginationDock}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(value) => {
              setItemsPerPage(value);
              setCurrentPage(1);
            }}
            itemsPerPageOptions={[20, 50, 100]}
          />
        </div>
      )}
    </div>
  );
};

export default AuditLogs;

